// `/ws/voice` sends `{type: "audio", payload}`: base64 PCM16 LE mono.

/** Mirrors `AUDIO_SAMPLE_RATE` in brian `modules/voice/protocol.py`; a mismatch plays pitch-shifted, not an error. */
export const VOICE_SAMPLE_RATE = 24000;

/** Pinned so decimation to 24kHz is an exact 2:1; `public/pcm-worklet.js` cannot interpolate. */
export const CAPTURE_SAMPLE_RATE = 48000;

export function decodeBase64Pcm16(payload: string): Int16Array {
  const binary = atob(payload);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  // A trailing odd byte would throw and kill the playback pump mid-reply.
  const samples = Math.floor(bytes.byteLength / 2);
  return new Int16Array(bytes.buffer, 0, samples);
}

function pcm16ToFloat32(samples: Int16Array): Float32Array<ArrayBuffer> {
  const floats = new Float32Array(new ArrayBuffer(samples.length * 4));
  for (let i = 0; i < samples.length; i++) {
    const sample = samples[i];
    floats[i] = sample < 0 ? sample / 0x8000 : sample / 0x7fff;
  }
  return floats;
}

export function pcmIsAudible(samples: Int16Array): boolean {
  if (samples.length === 0) return false;
  let energy = 0;
  for (const sample of samples) energy += sample * sample;
  return energy / samples.length >= 100 * 100;
}

interface PlaybackContext {
  readonly currentTime: number;
  createBuffer(
    channels: number,
    length: number,
    sampleRate: number,
  ): AudioBuffer;
  createBufferSource(): AudioBufferSourceNode;
  readonly destination: AudioNode;
}

/**
 * Chunks arrive faster than real time, so each starts at the previous one's end, floored to `currentTime` after a stall.
 * `flush` is barge-in: queued audio for a reply the provider cut would talk over the interrupter.
 */
export class PlaybackQueue {
  private cursor = 0;
  private audibleSpans: { start: number; end: number }[] = [];
  private live = new Set<AudioBufferSourceNode>();

  constructor(
    private readonly ctx: PlaybackContext,
    private readonly sampleRate: number = VOICE_SAMPLE_RATE,
  ) {}

  enqueue(samples: Int16Array): void {
    if (samples.length === 0) return;
    const buffer = this.ctx.createBuffer(1, samples.length, this.sampleRate);
    buffer.copyToChannel(pcm16ToFloat32(samples), 0);
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.ctx.destination);
    const startAt = Math.max(this.cursor, this.ctx.currentTime);
    this.audibleSpans = this.audibleSpans.filter(
      (span) => span.end > this.ctx.currentTime,
    );
    source.onended = () => this.live.delete(source);
    this.live.add(source);
    source.start(startAt);
    this.cursor = startAt + samples.length / this.sampleRate;
    if (pcmIsAudible(samples))
      this.audibleSpans.push({ start: startAt, end: this.cursor });
  }

  flush(): void {
    for (const source of this.live) {
      source.onended = null;
      try {
        source.stop();
      } catch {
        // Already finished; `stop` on a stopped source throws in some engines.
      }
    }
    this.live.clear();
    this.audibleSpans = [];
    this.cursor = 0;
  }

  // GPT-Live streams silent frames too; queued audio is not necessarily speech.
  get audible(): boolean {
    this.audibleSpans = this.audibleSpans.filter(
      (span) => span.end > this.ctx.currentTime,
    );
    return this.audibleSpans.some((span) => span.start <= this.ctx.currentTime);
  }

  get hasPendingSpeech(): boolean {
    return this.audibleSpans.some((span) => span.end > this.ctx.currentTime);
  }

  get speaking(): boolean {
    return this.live.size > 0;
  }
}
